import React, { useState } from 'react';
import { Drawer, Descriptions, Tag, Spin, Button, Space, Input, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  GET_AB_ADDRESS_MAPPING_BY_ID,
  GetAbAddressMappingByIdResponse,
} from '../../../wms/abAddress/graphql/abAddressGraphql';
import {
  REVIEW_AB_ADDRESS_MAPPING,
  ReviewAbAddressMappingResponse,
} from '../graphql/abAddressReviewGraphql';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from '../../../../constants/resourceCodes';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';

export type AbAddressReviewDrawerMode = 'view' | 'review';

interface AbAddressReviewDrawerProps {
  open: boolean;
  id: string | null;
  mode: AbAddressReviewDrawerMode;
  onClose: () => void;
  onReviewed?: () => void;
}

const reviewStatusTag = (status: number, t: (k: string) => string) => {
  switch (status) {
    case 1:
      return <Tag color="green">{t('mapTool.abAddressReview.status.approved')}</Tag>;
    case 2:
      return <Tag color="red">{t('mapTool.abAddressReview.status.rejected')}</Tag>;
    case 0:
    default:
      return <Tag color="orange">{t('mapTool.abAddressReview.status.pending')}</Tag>;
  }
};

const AbAddressReviewDrawer: React.FC<AbAddressReviewDrawerProps> = ({
  open,
  id,
  mode,
  onClose,
  onReviewed,
}) => {
  const { t } = useTranslation();
  const [reviewNote, setReviewNote] = useState('');

  const { data, loading } = useQuery<GetAbAddressMappingByIdResponse>(
    GET_AB_ADDRESS_MAPPING_BY_ID,
    {
      variables: { id: id ? Number(id) : undefined },
      skip: !open || !id,
      fetchPolicy: 'no-cache',
    }
  );

  const [reviewMapping, { loading: reviewing }] = useMutation<ReviewAbAddressMappingResponse>(
    REVIEW_AB_ADDRESS_MAPPING,
    {
      onCompleted: () => {
        message.success(t('mapTool.abAddressReview.messages.reviewSuccess') as string);
        onReviewed?.();
      },
      onError: (err) => message.error(translateGraphQLError(err, 'errors.system.unknownError')),
    }
  );

  const record = data?.getAbAddressMappingById;
  const dash = '—';
  const fmt = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : dash);
  const isPending = record?.reviewStatus === 0;
  const canReview = mode === 'review' && isPending;

  const handleReview = (approved: boolean) => {
    if (!id) return;
    reviewMapping({
      variables: {
        input: {
          id: Number(id),
          approved,
          reviewNote: reviewNote.trim() || undefined,
        },
      },
    });
  };

  const drawerTitle =
    mode === 'review'
      ? (t('mapTool.abAddressReview.reviewTitle') as string)
      : (t('mapTool.abAddressReview.detailTitle') as string);

  return (
    <Drawer
      title={drawerTitle}
      open={open}
      onClose={onClose}
      width={640}
      destroyOnClose
      afterOpenChange={(visible) => {
        if (!visible) setReviewNote('');
      }}
      footer={
        canReview ? (
          <HasResource resourceCodes={[ResourceCode.menu.AB_ADDRESS_REVIEW_SUBMIT]}>
            <Space className="w-full justify-end">
              <Button onClick={onClose}>{t('components.button.cancel')}</Button>
              <Button danger loading={reviewing} onClick={() => handleReview(false)}>
                {t('mapTool.abAddressReview.actions.reject')}
              </Button>
              <Button type="primary" loading={reviewing} onClick={() => handleReview(true)}>
                {t('mapTool.abAddressReview.actions.approve')}
              </Button>
            </Space>
          </HasResource>
        ) : null
      }
    >
      <Spin spinning={loading || reviewing}>
        {record && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('mapTool.abAddressReview.table.id')}>
                {record.id ?? dash}
              </Descriptions.Item>
              <Descriptions.Item label={t('mapTool.abAddressReview.table.hub')}>
                {record.hubName ?? record.hubId ?? dash}
              </Descriptions.Item>
              <Descriptions.Item label={t('mapTool.abAddressReview.table.aAddress')}>
                {record.aFullAddress ?? dash}
              </Descriptions.Item>
              <Descriptions.Item label={t('mapTool.abAddressReview.table.bAddress')}>
                {record.bFullAddress ?? dash}
              </Descriptions.Item>
              <Descriptions.Item label={t('mapTool.abAddressReview.form.reason')}>
                {record.reason ?? dash}
              </Descriptions.Item>
              <Descriptions.Item label={t('mapTool.abAddressReview.table.reporter')}>
                {record.reporterName ?? record.reporterAccountId ?? dash}
              </Descriptions.Item>
              <Descriptions.Item label={t('mapTool.abAddressReview.table.status')}>
                {reviewStatusTag(record.reviewStatus, t)}
              </Descriptions.Item>
              {!canReview && (
                <>
                  <Descriptions.Item label={t('mapTool.abAddressReview.detail.reviewer')}>
                    {record.reviewerName ?? dash}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('mapTool.abAddressReview.detail.reviewNote')}>
                    {record.reviewNote ?? dash}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('mapTool.abAddressReview.detail.reviewedAt')}>
                    {fmt(record.reviewedAt)}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={t('mapTool.abAddressReview.table.createdAt')}>
                {fmt(record.createdAt)}
              </Descriptions.Item>
            </Descriptions>
            {canReview && (
              <div className="mt-4">
                <div className="mb-2 text-sm text-gray-600">
                  {t('mapTool.abAddressReview.form.reviewNote')}
                </div>
                <Input.TextArea
                  rows={3}
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder={t('mapTool.abAddressReview.form.reviewNotePlaceholder') as string}
                />
              </div>
            )}
          </>
        )}
      </Spin>
    </Drawer>
  );
};

export default AbAddressReviewDrawer;
